import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ActivityIndicator } from 'react-native';
import { FileMessage } from '@sendbird/chat/message';
import { getAvailableUriFromFileMessage } from '@sendbird/uikit-utils';
import { lightGrey, green } from '@/shared/styles/colors';
import SentryService from '@/services/sentry/Sentry.service';
import { useGetUser } from '@/shared/hooks/useGetUser';

const MAX_RETRIES = 1;

interface ChatImageProps {
  message: FileMessage;
  maxWidth?: number;
  maxHeight?: number;
  /** When true, omits the outer MessageWrapper for use inside MessageContent's renderMessageBody */
  embedded?: boolean;
}

const ChatImage: React.FC<ChatImageProps> = ({
  message,
  maxWidth = 300,
  maxHeight = 400,
  embedded = false,
}) => {
  const { user } = useGetUser();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const fileUrl = getAvailableUriFromFileMessage(message);
  if (!fileUrl) {
    return null;
  }

  const fileName = message.name || 'Image';
  const isMyMessage = user?.id?.toString() === message.sender?.userId;

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    let naturalWidth = img.naturalWidth;
    let naturalHeight = img.naturalHeight;
    if (naturalWidth <= 0 || naturalHeight <= 0) {
      naturalWidth = maxWidth;
      naturalHeight = maxHeight;
    }
    const aspectRatio = naturalWidth / naturalHeight;

    let finalWidth = naturalWidth;
    let finalHeight = naturalHeight;

    // Scale down to fit within maxWidth/maxHeight while preserving aspect ratio
    if (naturalWidth > maxWidth) {
      finalWidth = maxWidth;
      finalHeight = maxWidth / aspectRatio;
    }

    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = maxHeight * aspectRatio;
    }

    setImageDimensions({ width: finalWidth, height: finalHeight });
    setIsLoading(false);
  };

  const handleImageError = () => {
    if (retryCount >= MAX_RETRIES) {
      SentryService.captureError('Error loading chat image', {
        extra: { url: fileUrl, fileName },
      });
      setHasError(true);
      setIsLoading(false);
      return;
    }
    setRetryCount((c) => c + 1);
    setHasError(false);
    setIsLoading(true);
    setImageDimensions(null);
  };

  const imageStyle = {
    display: isLoading ? 'none' : 'block',
    width: imageDimensions?.width,
    height: imageDimensions?.height,
  };

  const imageContent = (
    <>
      {hasError ? (
        <ErrorContainer>
          <ErrorText>Unable to load image</ErrorText>
        </ErrorContainer>
      ) : (
        <ImageContainer>
          {isLoading && (
            <LoadingContainer>
              <ActivityIndicator size="small" color={green} />
            </LoadingContainer>
          )}
          <StyledImage
            key={`${message.messageId}-${retryCount}`}
            src={fileUrl}
            alt={fileName}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={imageStyle}
          />
        </ImageContainer>
      )}
    </>
  );

  if (embedded) {
    return imageContent;
  }

  return (
    <MessageWrapper isMyMessage={isMyMessage}>{imageContent}</MessageWrapper>
  );
};

export default ChatImage;

const MessageWrapper = styled.div<{ isMyMessage: boolean }>(
  ({ isMyMessage }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 4,
    paddingBottom: 4,
  }),
);

const ImageContainer = styled.div({
  position: 'relative',
  marginTop: 4,
  marginBottom: 4,
  borderRadius: 8,
  overflow: 'hidden',
  display: 'inline-block',
});

const StyledImage = styled.img({
  display: 'block',
  objectFit: 'contain',
  backgroundColor: lightGrey,
  borderRadius: 8,
});

const LoadingContainer = styled.div({
  width: 100,
  height: 100,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: lightGrey,
  borderRadius: 8,
});

const ErrorContainer = styled.div({
  padding: 16,
  backgroundColor: lightGrey,
  borderRadius: 8,
  display: 'inline-block',
});

const ErrorText = styled.span({
  fontSize: 12,
  color: '#666',
});
